import { useTranslations } from 'next-intl';
import { useId, useState } from 'react';

import type { PromptModalProps } from '@/types/modal';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import BaseModal from './BaseModal';

type ExtendedPromptModalProps = PromptModalProps & {
  // Single-field form mode. When `onSubmit` is provided the modal renders an
  // <Input> with label/validation/Submit. When omitted the modal falls back to
  // the legacy `children` panel.
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  validate?: (value: string) => string | undefined;
  onSubmit?: (value: string) => void;
  cancelText?: string;
};

function PromptModal(props: ExtendedPromptModalProps) {
  const translate = useTranslations();

  const {
    title,
    promptText,
    open,
    children,
    onOpenChange,
    isCritical = false,
    description,
    className,
    footer,
    label,
    placeholder,
    defaultValue,
    value,
    onValueChange,
    validate,
    onSubmit,
    cancelText,
  } = props;

  const buttonText = promptText ?? translate('modal.continue');
  const finalCancelText = cancelText ?? translate('modal.cancel');

  const isFormMode = onSubmit !== undefined;
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');
  const currentValue = isControlled === true ? (value as string) : internalValue;

  const validationMessage
    = validate !== undefined ? validate(currentValue) : undefined;
  const isValid = validationMessage === undefined;

  // Stable ids for label/input/error association.
  const inputId = useId();
  const errorId = useId();

  const handleChange = (next: string) => {
    if (isControlled === false) {
      setInternalValue(next);
    }
    if (onValueChange !== undefined) {
      onValueChange(next);
    }
  };

  const handleSubmit = () => {
    if (isFormMode === false || isValid === false) {
      return;
    }
    onSubmit?.(currentValue);
  };

  const renderFormBody = () => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="flex flex-col gap-1.5"
    >
      {label !== undefined && (
        <label className="type-label-l text-foreground" htmlFor={inputId}>
          {label}
        </label>
      )}
      <Input
        id={inputId}
        value={currentValue}
        onChange={(e) => {
          handleChange(e.target.value);
        }}
        placeholder={placeholder}
        variant={isValid === false ? 'error' : 'default'}
        aria-invalid={isValid === false ? true : undefined}
        aria-describedby={isValid === false ? errorId : undefined}
      />
      {validationMessage !== undefined && (
        <span id={errorId} className="type-body-s text-error">
          {validationMessage}
        </span>
      )}
    </form>
  );

  const renderFooter = () => {
    if (footer !== undefined) {
      return footer;
    }
    if (isFormMode === true) {
      return (
        <>
          <Button
            type="button"
            variant="text"
            size="default"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {finalCancelText}
          </Button>
          <Button
            type="button"
            variant={isCritical === true ? 'error' : 'primary'}
            size="default"
            disabled={isValid === false}
            onClick={handleSubmit}
          >
            {buttonText}
          </Button>
        </>
      );
    }
    return (
      <Button
        type="button"
        variant={isCritical === true ? 'outline' : 'primary'}
        size="default"
        onClick={() => {
          onOpenChange(false);
        }}
      >
        {buttonText}
      </Button>
    );
  };

  return (
    <BaseModal
      onOpenChange={onOpenChange}
      open={open}
      title={title}
      description={description}
      className={className}
      danger={isCritical}
      footer={renderFooter()}
    >
      {isFormMode === true ? renderFormBody() : children}
    </BaseModal>
  );
}

export default PromptModal;
